import React from 'react'
import { Button } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { EnterpriseGrowthEvents, EventTypes, PageSource } from '@mc/track-event'
import Image from 'components/LegacyImage'
import { AutoplayVideo } from './AutoplayVideo'
import { CheckMarkItem } from './CheckMarkItem'
import { HeroCTAs } from './HeroCTAs'
import { PricingTiers } from './PricingSection'
import { SectionHeading } from './SectionHeading'
import { SharedFaq } from './SharedFaq'
import { SolutionsSection } from './SolutionsSection'
import { StatisticsCards } from './StatisticsCard'
import { VideoContainer } from './VideoContainer'
import Deloitte from './assets/deloitte.svg'
import Microsoft from './assets/microsoft.svg'
import PayPal from './assets/paypal.svg'
import Square from './assets/square.svg'
import { ContactForm } from './contact-form'
import { trackEnterpriseContactUsClicked } from './events'
import styles from '../index.module.scss'

export const ForBusinessBody = () => {
  const { t } = useTranslation('@mc/next')

  return (
    <div className='d-flex flex-column container mc-mt-10'>
      <div className='d-flex flex-column align-items-center mc-text--center'>
        <Image
          src='https://static.masterclass.com/for-business-hero.png'
          width={1768}
          height={272}
          alt=''
        />
        <h1 className='mc-font-headline1 mc-mt-10 col-md-8'>
          {t('forBusiness.heading')}
        </h1>
        <h2 className='mc-text-large col-md-6 mc-my-5'>
          {t('forBusiness.subheading')}
        </h2>
        <HeroCTAs />
      </div>
      <AutoplayVideo
        className='mc-my-12'
        mediaUuid='38c58716-6dc0-4d36-a39e-1f646d3df1dd'
      />
      <div className='mc-mt-6 mc-mb-12'>
        <SectionHeading label={t('forBusiness.aboutSection.title')}>
          {t('forBusiness.aboutSection.heading')}
        </SectionHeading>
        <p className='mc-text-large mc-text--center mc-text-color--medium mc-mt-6'>
          {t('forBusiness.aboutSection.subtitle')}
        </p>
        <div className='d-flex justify-content-center mc-mt-10'>
          <ul className='col-md-6'>
            <CheckMarkItem>
              {t('forBusiness.aboutSection.point1')}
            </CheckMarkItem>
            <CheckMarkItem>
              {t('forBusiness.aboutSection.point2')}
            </CheckMarkItem>
            <CheckMarkItem>
              {t('forBusiness.aboutSection.point3')}
            </CheckMarkItem>
          </ul>
        </div>
        <div className='d-flex justify-content-center mc-mt-8 mc-mb-2'>
          <Button render={<a href={paths.enterpriseHowItWorks} />}>
            {t('forBusiness.aboutSection.howItWorksButton')}
          </Button>
        </div>
      </div>
      <picture>
        <Image
          src='https://static.masterclass.com/catalog-computer.png'
          alt=''
          width={1141}
          height={631}
        />
      </picture>
      <SolutionsSection />
      <Container className='mc-my-10'>
        <SectionHeading label={t('forBusiness.pricingTiers.label')}>
          {t('forBusiness.pricingTiers.heading')}
        </SectionHeading>
        <PricingTiers pageSource={PageSource.MAW_HOME} />
      </Container>
      <Container className='mc-my-10'>
        <SectionHeading label={t('forBusiness.successStorySection.title')}>
          {t('forBusiness.successStorySection.subtitle')}
        </SectionHeading>
      </Container>
      <VideoContainer
        className='mc-my-9'
        mediaUuid='37679e1c-be7a-4a64-86d3-d10e961a8f3f'
      />
      <Container>
        <div className='mc-text--center mc-text--bold mc-text-large mc-text-color--medium mc-px-10'>
          {t('forBusiness.successStorySection.quote')}
        </div>
      </Container>
      <div className='mc-my-10'>
        <SectionHeading label={t('forBusiness.statsSection.title')}>
          {t('forBusiness.statsSection.subtitle')}
        </SectionHeading>
        <Container
          className={`uncontainer mc-mt-10 mc-p-6 ${styles.jockoCoverImage}`}
        >
          <div>
            <StatisticsCards />
            <div
              className={`d-flex flex-column mc-text--center mc-mt-8 mc-mt-lg-0`}
            >
              <div className='mc-font-headline2 mc-text--bold mc-mb-6'>
                {t('forBusiness.trusted')}
              </div>
              <div className='d-flex flex-wrap justify-content-center align-items-center mc-my-8 mc-text-color--medium mc-gap-5 mc-column-gap-10'>
                {' '}
                <PayPal />
                <Microsoft />
                <Square />
                <Deloitte />
              </div>
            </div>
          </div>
        </Container>
      </div>
      <Container className='mc-my-10'>
        <div
          id='contact-form'
          className='d-flex flex-column justify-content-center mc-bg-container-dark mc-p-9 mc-corners--lg mc-w-100 mc-mt-10'
        >
          <SectionHeading
            className='mc-mb-10'
            label={t('forBusiness.connectWithUs')}
          >
            {t('forBusiness.bringOut')}
          </SectionHeading>
          <ContactForm
            envName={process.env.NODE_ENV}
            onClickSubmit={() =>
              trackEnterpriseContactUsClicked({
                pageSource: PageSource.MAW_HOME,
              })
            }
          />
        </div>
      </Container>
      <SharedFaq
        brandedTitle={false}
        title={t('forBusiness.faq.title')}
        questions={[
          {
            question: t('forBusiness.faq.question1'),
            answer: 'faq.forBusiness.questions.0.answer',
          },
          {
            question: t('forBusiness.faq.question2'),
            answer: 'faq.forBusiness.questions.1.answer',
          },
          {
            question: t('faq.forBusiness.questions.2.question'),
            answer: 'faq.forBusiness.questions.2.answer',
          },
        ]}
        trackProps={{
          eventType: EnterpriseGrowthEvents.EVENTS.MAW_HOME_PAGE_CLICK,
          pageSource: EventTypes.FOR_BUSINESSES,
        }}
      />
    </div>
  )
}

const Container = ({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) => (
  <div className={`d-flex flex-wrap justify-content-center ${className ?? ''}`}>
    {children}
  </div>
)
